import { Check, ChevronDown, Filter, Link, Pencil, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { listingService } from '@/services/listing.service';
import type { PaginatedResponse } from '@/types/contractor';
import type { Listing, ListingQueryParams } from '@/types/listing';
import { ListingStatus } from '@/types/listing';

export function Listings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listingsData, setListingsData] = useState<PaginatedResponse<Listing> | null>(null);

  const filterOptions = ['All Status', 'Draft', 'Published', 'Archived'];

  // Get state from URL search params
  const searchQuery = searchParams.get('search') || '';
  const filterStatus = searchParams.get('status') || 'All Status';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Update URL search params
  const updateSearchParams = (updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'All Status' || value === 1) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  // Fetch listings
  const fetchListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: ListingQueryParams = {
        page: currentPage,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (filterStatus === 'Draft') {
        params.status = ListingStatus.DRAFT;
      } else if (filterStatus === 'Published') {
        params.status = ListingStatus.PUBLISHED;
      } else if (filterStatus === 'Archived') {
        params.status = ListingStatus.ARCHIVED;
      }

      const data = await listingService.getAll(params);
      setListingsData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listings');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, filterStatus]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleCopyUrl = async (listing: Listing) => {
    const url = `${import.meta.env.VITE_WEB_URL}/listing-details/${listing.slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(listing._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleEdit = (id: string) => {
    navigate(`/listings/${id}/edit`);
  };

  const handleTogglePublic = async (listing: Listing) => {
    const newValue = listing.isPublic === false ? true : false;
    setTogglingId(listing._id);
    try {
      await listingService.update(listing._id, { isPublic: newValue });
      setListingsData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: prev.data.map((l) => (l._id === listing._id ? { ...l, isPublic: newValue } : l)),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update listing');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to archive this listing?')) {
      return;
    }

    try {
      await listingService.delete(id);
      fetchListings(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete listing');
    }
  };

  // Reset to page 1 when filter changes
  const handleFilterChange = (newFilter: string) => {
    updateSearchParams({ status: newFilter, page: 1, search: searchQuery });
  };

  const handleSearchChange = (query: string) => {
    updateSearchParams({ search: query, page: 1, status: filterStatus });
  };

  const handlePageChange = (page: number) => {
    updateSearchParams({ page, search: searchQuery, status: filterStatus });
  };

  const formatPrice = (price?: number) => {
    if (price === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusColor = (status: ListingStatus) => {
    switch (status) {
      case ListingStatus.PUBLISHED:
        return 'bg-[#d5eee9] text-[#055135]';
      case ListingStatus.DRAFT:
        return 'bg-[#fef3cd] text-[#856404]';
      case ListingStatus.ARCHIVED:
        return 'bg-[#f5f5f5] text-[#646464]';
      default:
        return 'bg-[#f5f5f5] text-[#646464]';
    }
  };

  const listings = listingsData?.data || [];
  const pagination = listingsData?.pagination;

  return (
    <div className="space-y-[32px]">
      {/* Search and Filter Bar */}
      <div className="flex items-center gap-[12px]">
        {/* Search Input */}
        <div className="relative w-[60%]">
          <Search className="absolute left-[16px] top-1/2 h-5 w-5 -translate-y-1/2 text-[#8d8c8f]" />
          <input
            type="text"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white pl-[48px] pr-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
          />
        </div>

        {/* Filter Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white pl-[16px] pr-[16px] text-[14px] font-medium text-[#181818] transition-all hover:border-[#646464] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
          >
            <Filter className="h-[18px] w-[18px] text-[#8d8c8f]" />
            <span className="whitespace-nowrap">{filterStatus}</span>
            <ChevronDown
              className={`h-[18px] w-[18px] text-[#8d8c8f] transition-transform ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute left-0 top-[52px] z-10 min-w-[180px] overflow-hidden rounded-[8px] border border-[#e5e5e5] bg-white shadow-lg">
              {filterOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    handleFilterChange(option);
                    setIsDropdownOpen(false);
                  }}
                  className={`flex w-full items-center gap-[8px] px-[16px] py-[12px] text-left text-[14px] transition-colors ${
                    filterStatus === option
                      ? 'bg-[#f5f5f5] font-semibold text-[#055135]'
                      : 'font-medium text-[#181818] hover:bg-[#fafafa]'
                  }`}
                >
                  {filterStatus === option && <span className="text-[#055135]">&#10003;</span>}
                  <span className={filterStatus === option ? '' : 'ml-[20px]'}>{option}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-[8px] border border-[#fca5a5] bg-[#fef2f2] p-[16px] text-[14px] text-[#b91c1c]">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-[64px]">
          <div className="h-[32px] w-[32px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
        </div>
      )}

      {/* Listings Table */}
      {!isLoading && (
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e5e5e5] bg-[#f5f5f5]">
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Property
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Location
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Price
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Details
                </th>
                <th className="px-[24px] py-[16px] text-center text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Public
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Status
                </th>
                <th className="px-[24px] py-[16px] text-right text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr
                  key={listing._id}
                  className="border-b border-[#e5e5e5] last:border-0 hover:bg-[#fafafa]"
                >
                  <td className="px-[24px] py-[20px]">
                    <div className="flex items-center gap-[12px]">
                      {listing.heroImage ? (
                        <img
                          src={listing.heroImage}
                          alt={listing.propertyTitle}
                          className="h-[48px] w-[64px] rounded-[6px] object-cover"
                        />
                      ) : (
                        <div className="flex h-[48px] w-[64px] items-center justify-center rounded-[6px] bg-[#f5f5f5] text-[12px] text-[#8d8c8f]">
                          No image
                        </div>
                      )}
                      <div>
                        <p className="text-[14px] font-semibold text-[#181818]">
                          {listing.propertyTitle}
                        </p>
                        <p className="text-[12px] text-[#646464]">{listing.address}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-[24px] py-[20px] text-[14px] text-[#646464]">
                    {listing.city}, {listing.state}
                  </td>
                  <td className="px-[24px] py-[20px] text-[14px] font-semibold text-[#181818]">
                    {formatPrice(listing.housePrice)}
                  </td>
                  <td className="px-[24px] py-[20px] text-[14px] text-[#646464]">
                    {listing.beds !== undefined && `${listing.beds} beds`}
                    {listing.baths !== undefined && ` · ${listing.baths} baths`}
                    {listing.squareFeet !== undefined &&
                      ` · ${listing.squareFeet.toLocaleString()} sqft`}
                    {listing.beds === undefined &&
                      listing.baths === undefined &&
                      listing.squareFeet === undefined &&
                      '-'}
                  </td>
                  <td className="px-[24px] py-[20px] text-center">
                    {togglingId === listing._id ? (
                      <div className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
                    ) : (
                      <input
                        type="checkbox"
                        checked={listing.isPublic !== false}
                        onChange={() => handleTogglePublic(listing)}
                        className="h-[18px] w-[18px] cursor-pointer rounded border-[#e5e5e5] accent-[#055135]"
                      />
                    )}
                  </td>
                  <td className="px-[24px] py-[20px]">
                    <span
                      className={`inline-flex items-center rounded-[6px] px-[12px] py-[4px] text-[12px] font-semibold ${getStatusColor(listing.status)}`}
                    >
                      {listing.status}
                    </span>
                  </td>
                  <td className="px-[24px] py-[20px]">
                    <div className="flex items-center justify-end gap-[8px]">
                      <button
                        type="button"
                        onClick={() => handleEdit(listing._id)}
                        className="flex h-[36px] w-[36px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
                        aria-label="Edit listing"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyUrl(listing)}
                        className="flex h-[36px] w-[36px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
                        aria-label="Copy listing URL"
                      >
                        {copiedId === listing._id ? (
                          <Check size={16} className="text-[#055135]" />
                        ) : (
                          <Link size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(listing._id)}
                        className="flex h-[36px] w-[36px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#fee] hover:text-[#d4183d]"
                        aria-label="Delete listing"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {listings.length === 0 && (
            <div className="py-[64px] text-center">
              <p className="text-[14px] text-[#8d8c8f]">No listings found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-[4px] py-[12px]">
          <div className="text-[13px] text-[#8d8c8f]">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
            results
          </div>
          <div className="flex items-center gap-[6px]">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="flex h-[32px] items-center gap-[4px] rounded-[6px] px-[12px] text-[13px] font-medium text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#646464]"
            >
              Previous
            </button>
            <div className="flex items-center gap-[2px]">
              {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => handlePageChange(page)}
                    className={`flex h-[32px] w-[32px] items-center justify-center rounded-[6px] text-[13px] font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-[#055135] text-white'
                        : 'text-[#646464] hover:bg-[#f5f5f5] hover:text-[#181818]'
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNext}
              className="flex h-[32px] items-center gap-[4px] rounded-[6px] px-[12px] text-[13px] font-medium text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#646464]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
